import type { CartCheckoutPanelProps } from '@engyalo/delivery-ui-components'
import {
  SessionCheckoutRule,
  SessionCheckoutRuleType,
} from '../graphQL/queries/getSession/types'
import type { RootState } from '../redux/store'
import { isHidePricesEnabled } from './hidePrices'

const optional = <T>(value: T | null | undefined): T | undefined =>
  value ?? undefined

/**
 * The panel props this adapter owns, checked against the library's own
 * prop shape.
 *
 * The same guard `getOrderConfirmationPanelProps` uses one level up, and it
 * catches the same class of drift: the injector is registered in a `Map`
 * whose value type is `UnresolvedRecord`, so a library rename of
 * `expectedDeliveryDate` or `paymentMethod` would compile, land unread on the
 * panel, and silently drop the row.
 *
 * **It guards names and value types, not presence.** Every panel prop is
 * optional, so `{}` satisfies this type — dropping a line from the returned
 * object is a compile *success*. That case is covered by the colocated
 * assertions instead, which is why each forwarded prop has a test naming it.
 */
type InjectedCartCheckoutProps = Pick<
  CartCheckoutPanelProps,
  'expectedDeliveryDate' | 'paymentMethod' | 'hidePrices'
>

/**
 * The pointwise mapper: the session's selection array collapsed to the two
 * scalars the panel actually reads.
 *
 * `null` or empty in, `undefined` out — the panel's own two props are
 * optional and it drops each row for an absent value (COM-852), so returning
 * `undefined` here is the same story one level down. That is the pilot's
 * normal state until the write path exists (OQ-7 on COM-849).
 *
 * `setCheckoutRuleById` filters and appends, so the array holds at most one
 * entry per `ruleType`. `find` reflects that. A second entry for the same
 * type would be silently ignored — which is the right shape for a defensive
 * read: cinnamon has no authority to reject a shape the server accepted, and
 * picking either would be a coin toss.
 *
 * **Nothing is normalised here.** The delivery `data` is a `YYYY-MM-DD`
 * calendar-day string and reaches the panel raw — `CartCheckoutPanel` uses
 * `formatOrderCalendarDay` which pins the date to UTC, and applying a
 * viewer's zone to it moves it west of UTC to the previous day (the COM-816
 * defect rather than its fix). The payment `data` is a raw backend enum
 * value the panel maps through `paymentMethodLabels`, authored per
 * storefront — cinnamon does not translate either.
 *
 * ⚠️ The array's `value` is a nullable `SessionCheckoutRuleValue`, so the
 * chain is `?.value?.data` rather than `.value.data`. A rule entry the
 * storefront wrote with no `value` yet reaches here as `{ ruleType, value:
 * null }`, and reading `.value.data` on it throws.
 */
export const toCartCheckoutPanelSelection = (
  rules?: SessionCheckoutRule[] | null,
): InjectedCartCheckoutProps | undefined => {
  if (!Array.isArray(rules) || rules.length === 0) return undefined

  const findData = (ruleType: `${SessionCheckoutRuleType}`): string | undefined => {
    const match = rules.find((rule) => rule?.ruleType === ruleType)
    return optional(match?.value?.data)
  }

  return {
    expectedDeliveryDate: findData(SessionCheckoutRuleType.DELIVERY_DATES),
    paymentMethod: findData(SessionCheckoutRuleType.PAYMENT_METHODS),
    // `hidePrices` belongs to the injector, not the pointwise mapper — the
    // mapper only knows the rules array. Left absent here so the injector
    // spreads it in from `RootState` below.
  }
}

/**
 * The three `CartCheckoutPanel` props cinnamon owns rather than the config
 * author.
 *
 * Injected in `useCinnamonProps` for any component whose `importRoute` is
 * `CartCheckoutPanel`, exactly as `getOrderConfirmationPanelProps` is for
 * the confirmation screen. None of these is authorable as a `{{variable}}`:
 * two are derived from the session's selections (a shape the config DSL
 * cannot build) and one — `hidePrices` — is derived from a state predicate
 * the config DSL cannot compute.
 *
 * Everything else stays in config — every label, the helper lines, the
 * icons, the icon set, the currency, the payment-method label map, the
 * colour props, `fieldsToDisplay` and the custom slots. This file adds no
 * layout or copy decision.
 *
 * Injected **before** `processData` and `maskPricesForDisplay` run, so slot
 * text is masked with everything else — same seam as `orderDetailPanel` and
 * `orderConfirmationPanel`.
 *
 * **No `timeZone` on this panel.** `CartCheckoutPanel` uses
 * `formatOrderCalendarDay` for the delivery date, which pins to UTC on
 * purpose; the panel has no `timeZone` prop at all (COM-859). That is
 * deliberate rather than an oversight — see `toCartCheckoutPanelSelection`
 * for why the reverse ships the COM-816 defect.
 *
 * **No `locale` either.** The library falls back to the runtime default when
 * the prop is absent, and the tenant's locale is already authorable as
 * `{{config.options.language}}` or a text token — cinnamon does not know one
 * that is not on the config.
 */
export const getCartCheckoutPanelProps = (
  state: RootState,
): InjectedCartCheckoutProps => {
  const selection = toCartCheckoutPanelSelection(
    state.defaultSlice.sessionData?.checkoutRules,
  )
  return {
    expectedDeliveryDate: selection?.expectedDeliveryDate,
    paymentMethod: selection?.paymentMethod,
    /**
     * Slot text is a string, and `maskPricesForDisplay` is an allowlist of
     * prop names over numbers, so it cannot see a price inside a slot's
     * `'Precio por kilo $98.90'`. This panel renders no money of its own —
     * a delivery date and a payment *method* are not amounts — but the two
     * custom slots can reach any field on the shopper's session, and the
     * flag is what stops one from leaking a total.
     */
    hidePrices: isHidePricesEnabled(state),
  }
}

export default getCartCheckoutPanelProps
